import { useEffect, useState } from 'react';
import { compact } from 'lodash-es';
import { useOverlayContext, ConfirmationModal, Typography } from 'seagull';
import { useContactActions } from "../../hooks/useContactActions";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useOnMount } from "../../hooks/useOnMount";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import { handlePromise } from "../../infra/utils";
import { getAccountingDetails } from "../../integrations/Accounting/accounting.utils";
import AccountingCustomerDropdown from "../../integrations/Accounting/AccountingCustomerDropdown/AccountingCustomerDropdown";
import { OptionType } from "../../integrations/Accounting/AccountingCustomerDropdown/AccountingCustomerDropdown.utils";
import { NewContactModel } from "../../models/ContactModel";
import { selectors } from "../../store/state-selectors";
import { useStore, useStoreActions } from "../../store/store-context";
import { Accounting } from "../../types/api.model";
import { ContactModalType, ManageContactModal } from "./index";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export function ManageContactModalForVendor(props) {
  var _ref, _existingContact$firs, _ref2, _existingContact$last, _ref3, _existingContact$comp, _ref4, _existingContact$emai, _ref5, _existingContact$phon, _existingContact$hasS, _existingContact$isMu;
  const {
    existingContact,
    newContactToCreate,
    origin,
    markPending,
    markComplete
  } = props;
  const overlayContext = useOverlayContext();
  const {
    api,
    fetchAccountingCustomers,
    fetchIntegrationsSummary
  } = useStoreActions();
  const {
    loading: qboLoading,
    data: qboCustomers
  } = useStore(state => state.qboCustomers);
  const {
    loading: xeroLoading,
    data: xeroCustomers
  } = useStore(state => state.xeroCustomers);
  const {
    fetchContactProposals,
    withdrawToEditContactProposals
  } = useContactActions();
  const [qboCustomerOption, setQboCustomerOption] = useState(null);
  const [xeroCustomerOption, setXeroCustomerOption] = useState(null);
  const [contactProposals, setContactProposals] = useState([]);
  const isLinkedToQbo = useStore(selectors.integration.isLinkedToQbo);
  const isLinkedToXero = useStore(selectors.integration.isLinkedToXero);
  const qboAccountingName = getAccountingDetails(Accounting.QBO).fullName;
  const xeroAccountingName = getAccountingDetails(Accounting.Xero).fullName;
  const {
    ALLOW_EDIT_CONTACT
  } = useFeatureFlags();
  const contact = {
    firstName: (_ref = (_existingContact$firs = existingContact === null || existingContact === void 0 ? void 0 : existingContact.firstName()) !== null && _existingContact$firs !== void 0 ? _existingContact$firs : newContactToCreate === null || newContactToCreate === void 0 ? void 0 : newContactToCreate.firstName) !== null && _ref !== void 0 ? _ref : '',
    lastName: (_ref2 = (_existingContact$last = existingContact === null || existingContact === void 0 ? void 0 : existingContact.lastName()) !== null && _existingContact$last !== void 0 ? _existingContact$last : newContactToCreate === null || newContactToCreate === void 0 ? void 0 : newContactToCreate.lastName) !== null && _ref2 !== void 0 ? _ref2 : '',
    companyName: (_ref3 = (_existingContact$comp = existingContact === null || existingContact === void 0 ? void 0 : existingContact.companyName()) !== null && _existingContact$comp !== void 0 ? _existingContact$comp : newContactToCreate === null || newContactToCreate === void 0 ? void 0 : newContactToCreate.companyName) !== null && _ref3 !== void 0 ? _ref3 : '',
    email: (_ref4 = (_existingContact$emai = existingContact === null || existingContact === void 0 ? void 0 : existingContact.email()) !== null && _existingContact$emai !== void 0 ? _existingContact$emai : newContactToCreate === null || newContactToCreate === void 0 ? void 0 : newContactToCreate.email) !== null && _ref4 !== void 0 ? _ref4 : '',
    phone: (_ref5 = (_existingContact$phon = existingContact === null || existingContact === void 0 ? void 0 : existingContact.phone()) !== null && _existingContact$phon !== void 0 ? _existingContact$phon : newContactToCreate === null || newContactToCreate === void 0 ? void 0 : newContactToCreate.phone) !== null && _ref5 !== void 0 ? _ref5 : ''
  };
  const hasContact = existingContact && existingContact.id();
  const modalType = hasContact ? ContactModalType.Edit : ContactModalType.Create;
  const isEmailReadonly = Boolean(existingContact && !existingContact.isEmailEditable());
  useOnMount(() => {
    if (isLinkedToQbo) {
      Promise.all([fetchAccountingCustomers(Accounting.QBO), fetchIntegrationsSummary()]);
    }
    if (isLinkedToXero) {
      Promise.all([fetchAccountingCustomers(Accounting.Xero), fetchIntegrationsSummary()]);
    }
    if (existingContact && !isEmailReadonly) {
      fetchContactProposals(existingContact.id()).then(setContactProposals);
    }
  });
  useEffect(() => {
    if (existingContact !== null && existingContact !== void 0 && existingContact.integratedId({
      accounting: Accounting.QBO
    })) {
      const mappedQboCustomer = qboCustomers === null || qboCustomers === void 0 ? void 0 : qboCustomers.find(qboCustomer => qboCustomer.id() === existingContact.integratedId({
        accounting: Accounting.QBO
      }));
      if (mappedQboCustomer) {
        setQboCustomerOption({
          type: OptionType.Existing,
          customer: mappedQboCustomer
        });
      }
    }
  }, [qboCustomers]);
  useEffect(() => {
    if (existingContact !== null && existingContact !== void 0 && existingContact.integratedId({
      accounting: Accounting.Xero
    })) {
      const mappedXeroCustomer = xeroCustomers === null || xeroCustomers === void 0 ? void 0 : xeroCustomers.find(xeroCustomer => xeroCustomer.id() === existingContact.integratedId({
        accounting: Accounting.Xero
      }));
      if (mappedXeroCustomer) {
        setXeroCustomerOption({
          type: OptionType.Existing,
          customer: mappedXeroCustomer
        });
      }
    }
  }, [xeroCustomers]);
  const onSubmitContactV1 = async contactToSave => {
    if (existingContact && contactToSave.email !== (existingContact === null || existingContact === void 0 ? void 0 : existingContact.email()) && contactProposals.length > 0) {
      const shouldWithdrawProposals = await openWithdrawToProposalModal();
      if (existingContact && shouldWithdrawProposals) {
        await withdrawToEditContactProposals(existingContact.id());
      } else {
        return;
      }
    }
    const result = await saveContactRequest(contactToSave);
    if (result.success) {
      return await handleUpdateIntegrations({
        success: true,
        data: {
          id: result.data.id(),
          version: result.data.version()
        },
        error: undefined
      });
    } else {
      return await handleUpdateIntegrations(result);
    }
  };
  const handleUpdateIntegrations = async savedContact => {
    if (!savedContact.success) {
      const message = new ErrorResponse(savedContact.error).saveContactErrorText();
      overlayContext.showErrorToast(message, 'save-contact-error-toast');
    } else {
      const {
        id,
        version
      } = savedContact.data;
      if (savedContact && isLinkedToQbo) {
        await handleAccountingMapping(id, version, Accounting.QBO);
      }
      if (savedContact && isLinkedToXero) {
        await handleAccountingMapping(id, version, Accounting.Xero);
      }
      return {
        id,
        version
      };
    }
  };
  const openWithdrawToProposalModal = () => {
    return overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref6 => {
        let {
          dismissModal
        } = _ref6;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          skin: "danger",
          title: "This client has proposals awaiting approval",
          content: /*#__PURE__*/_jsx(_Fragment, {
            children: /*#__PURE__*/_jsx(Typography, {
              children: "Making changes to the client's email address will automatically withdraw pending proposals. Withdrawn proposals will be saved in your drafts."
            })
          }),
          textAlignCenter: false,
          onPrimaryButtonClick: () => dismissModal(true),
          onSecondaryButtonClick: () => dismissModal(false),
          primaryButtonLabel: "Save changes and withdraw proposals",
          secondaryButtonLabel: "Back",
          primaryButtonTestid: "confirm-withdraw",
          secondaryButtonTestid: "cancel-withdraw",
          "data-testid": "confirm-withdraw-modal"
        });
      }
    });
  };
  const openUpdateAnchorUserEmailModal = () => {
    return overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref7 => {
        let {
          dismissModal
        } = _ref7;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          skin: "danger",
          title: "Making changes to an existing user in Anchor",
          content: /*#__PURE__*/_jsx(_Fragment, {
            children: /*#__PURE__*/_jsx(Typography, {
              children: "You are about to update the email address of an existing user in Anchor. The user will need to use the updated email address to access their client portal."
            })
          }),
          textAlignCenter: false,
          onPrimaryButtonClick: () => dismissModal(true),
          onSecondaryButtonClick: () => dismissModal(false),
          primaryButtonLabel: "Proceed anyway",
          secondaryButtonLabel: "Cancel",
          primaryButtonTestid: "confirm-update-anchor-user-email",
          secondaryButtonTestid: "cancel-update-anchor-user-email",
          "data-testid": "confirm-update-anchor-user-email-modal"
        });
      }
    });
  };
  const handleAccountingMapping = async (contactId, version, accounting) => {
    try {
      const accountingCustomerOption = (() => {
        switch (accounting) {
          case Accounting.QBO:
            return qboCustomerOption;
          case Accounting.Xero:
            return xeroCustomerOption;
        }
      })();
      const shouldCreateNew = (accountingCustomerOption === null || accountingCustomerOption === void 0 ? void 0 : accountingCustomerOption.type) === OptionType.CreateNew;
      if (shouldCreateNew) {
        await api.accounting.createContact({
          contactId,
          accounting
        });
      } else {
        const isLinking = (accountingCustomerOption === null || accountingCustomerOption === void 0 ? void 0 : accountingCustomerOption.type) === OptionType.Existing;
        const newIntegratedId = isLinking ? accountingCustomerOption.customer.id() : undefined;
        const isDirty = (existingContact === null || existingContact === void 0 ? void 0 : existingContact.integratedId({
          accounting
        })) !== newIntegratedId;
        if (isDirty) {
          await api.accounting.linkContact({
            accounting,
            contactId,
            data: {
              version,
              integratedId: newIntegratedId
            }
          });
        }
      }
    } catch (err) {
      const isDuplicateError = new ErrorResponse(err).hasServerError(ServerError.DuplicateNameExists);
      overlayContext.showErrorToast(isDuplicateError ? "Could not create a new client in ".concat(getAccountingDetails(accounting).fullName, ". A client with these details already exists.") : 'ErrorText.UnexpectedErrorTryAgain', isDuplicateError ? 'duplicate-contact-toast' : 'general-error-toast');
    }
  };
  const updateContactDataV2 = async (contact, data) => {
    const contactToUpdate = contact.update(data);
    const userId = contact.userId(); // contact has relation to a user

    if (userId) {
      const hasEmailChanged = data.email !== contact.email();
      const hasNameChanged = data.firstName !== contact.firstName() || data.lastName !== contact.lastName();
      const hasPhoneChanged = data.phone !== contact.phone();
      const hasCompanyChanged = data.companyName !== contact.companyName();
      const hasAnyUserDataChanged = hasEmailChanged || hasNameChanged || hasPhoneChanged;
      const hasAnyDataChanged = hasAnyUserDataChanged || hasCompanyChanged;
      if (!hasAnyDataChanged) {
        return {
          success: true,
          data: {
            id: contactToUpdate.id(),
            version: contactToUpdate.version()
          },
          error: undefined
        };
      }
      const promises = [];
      if (hasEmailChanged) {
        promises.push(api.user.updateBuyerEmail(userId, data.email, contact.customerId()));
      }
      if (hasNameChanged || hasPhoneChanged) {
        promises.push(api.user.updateBuyer(userId, {
          name: {
            first: contactToUpdate.firstName(),
            last: contactToUpdate.lastName()
          },
          phone: contactToUpdate.phone(),
          customerBusinessId: contact.customerId()
        }));
      }
      if (hasCompanyChanged && contact.customerId()) {
        promises.push(api.business.updateBuyerCompany(contact.customerId(), contactToUpdate.companyName()));
      }
      return handlePromise(Promise.all(promises).then(() => ({
        id: contactToUpdate.id(),
        version: contactToUpdate.version()
      })));
    } else {
      const hasDataChanged = data.email !== contact.email() || data.firstName !== contact.firstName() || data.lastName !== contact.lastName() || data.companyName !== contact.companyName() || data.phone !== contact.phone();
      if (!hasDataChanged) {
        return {
          success: true,
          data: {
            id: contactToUpdate.id(),
            version: contactToUpdate.version()
          },
          error: undefined
        };
      }
      return handlePromise(api.contact.updateV2(contactToUpdate.id(), {
        firstName: contactToUpdate.firstName(),
        lastName: contactToUpdate.lastName(),
        companyName: contactToUpdate.companyName(),
        email: contactToUpdate.email(),
        phone: contactToUpdate.phone(),
        version: contactToUpdate.version()
      }).then(response => ({
        id: contactToUpdate.id(),
        version: response
      })));
    }
  };
  const saveContactRequest = data => {
    if (hasContact) {
      const contactToUpdate = existingContact.update(data);
      return handlePromise(api.contact.update(contactToUpdate));
    } else {
      return handlePromise(api.contact.create(NewContactModel.make(data)));
    }
  };
  const onCancelClick = async () => {
    let shouldCloseModal = true;
    if (newContactToCreate) {
      shouldCloseModal = await overlayContext.showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: _ref8 => {
          let {
            dismissModal
          } = _ref8;
          return /*#__PURE__*/_jsx(ConfirmationModal, {
            title: "Are you sure?",
            content: "All unsaved contact details will be lost",
            onPrimaryButtonClick: () => dismissModal(true),
            onSecondaryButtonClick: () => dismissModal(false),
            primaryButtonLabel: "Exit without saving",
            secondaryButtonLabel: "Cancel",
            primaryButtonTestid: "confirm",
            secondaryButtonTestid: "un-confirm",
            "data-testid": "confirm-losing-unsaved-data"
          });
        }
      });
    }
    return shouldCloseModal;
  };
  const onSubmitContactV2 = async contactToSave => {
    if (hasContact) {
      const hasEmailChanged = existingContact && contactToSave.email !== (existingContact === null || existingContact === void 0 ? void 0 : existingContact.email());
      const hasUserId = existingContact && existingContact.userId();
      if (hasEmailChanged && hasUserId) {
        const shouldProceed = await openUpdateAnchorUserEmailModal();
        if (!shouldProceed) {
          return;
        }
      }
      if (existingContact && contactToSave.email !== (existingContact === null || existingContact === void 0 ? void 0 : existingContact.email()) && contactProposals.length > 0) {
        const shouldWithdrawProposals = await openWithdrawToProposalModal();
        if (existingContact && shouldWithdrawProposals) {
          await withdrawToEditContactProposals(existingContact.id());
        } else {
          return;
        }
      }
      const userId = existingContact.userId();
      const shouldTrackPending = userId && hasEmailChanged;
      if (shouldTrackPending) {
        markPending === null || markPending === void 0 || markPending(userId);
      }
      const updatedContact = await updateContactDataV2(existingContact, contactToSave);
      if (!updatedContact.success && shouldTrackPending) {
        markComplete === null || markComplete === void 0 || markComplete(userId);
      }
      return await handleUpdateIntegrations(updatedContact);
    } else {
      const result = await handlePromise(api.contact.create(NewContactModel.make(contactToSave)));
      if (result.success) {
        return await handleUpdateIntegrations({
          success: true,
          data: {
            id: result.data.id(),
            version: result.data.version()
          },
          error: undefined
        });
      } else {
        return await handleUpdateIntegrations(result);
      }
    }
  };
  return /*#__PURE__*/_jsx(ManageContactModal, {
    onClose: props.onClose,
    contact: contact,
    modalType: modalType,
    onSaveContact: ALLOW_EDIT_CONTACT ? onSubmitContactV2 : onSubmitContactV1,
    emailEditState: {
      isEmailReadonly: !ALLOW_EDIT_CONTACT && isEmailReadonly,
      tooltipContent: !ALLOW_EDIT_CONTACT && isEmailReadonly ? 'Clients with whom you have an agreement cannot have their email addresses changed' : undefined
    },
    editingLimitations: {
      hasServiceProviderBusiness: (_existingContact$hasS = existingContact === null || existingContact === void 0 ? void 0 : existingContact.hasServiceProviderBusiness()) !== null && _existingContact$hasS !== void 0 ? _existingContact$hasS : false,
      isMultiVendorClient: (_existingContact$isMu = existingContact === null || existingContact === void 0 ? void 0 : existingContact.isMultiVendorClient()) !== null && _existingContact$isMu !== void 0 ? _existingContact$isMu : false
    },
    onCancelClick: onCancelClick,
    origin: origin,
    extraCellsContent: compact([isLinkedToQbo ? /*#__PURE__*/_jsx(AccountingCustomerDropdown, {
      accounting: Accounting.QBO,
      value: qboCustomerOption,
      onChange: val => setQboCustomerOption(val),
      accountingCustomers: qboCustomers || [],
      textFieldProps: {
        label: "".concat(qboAccountingName, " mapping")
      },
      loading: qboLoading,
      disabled: existingContact && existingContact.isSample()
    }) : null, isLinkedToXero ? /*#__PURE__*/_jsx(AccountingCustomerDropdown, {
      accounting: Accounting.Xero,
      value: xeroCustomerOption,
      onChange: val => setXeroCustomerOption(val),
      accountingCustomers: xeroCustomers || [],
      textFieldProps: {
        label: "".concat(xeroAccountingName, " mapping")
      },
      loading: xeroLoading,
      disabled: existingContact && existingContact.isSample()
    }) : null])
  });
}